import { z } from "zod";
import { compareDates, isSet, type PDate, todayLocal } from "@/plan-logic/lib/dates";
import { PriorityKind } from "@/generated/core/v1/family";
import {
  hasDbPension,
  members,
  type OnboardingDraft,
  pensionJob,
} from "@/plan-logic/questionnaire/draft";
import { jobStartIssue } from "@/plan-logic/questionnaire/jobStart";
import { validateOwed } from "@/plan-logic/questionnaire/owed";
import { draftRetirementAgeIssue } from "@/questionnaire/retirementAge";

/**
 * Instant feedback for the onboarding forms. Not authoritative: the engine validates the saved
 * questionnaire and its `ValidationFailure` errors are shown by field path (see Onboarding.tsx).
 * Every rule is a presence or range check on what the user typed (an age already reached is out of
 * range); none is a financial rule.
 */
export type Issue = { path: string; message: string };

const REQUIRED = "Required";

function fullDate(d: { year: number; month: number; day: number } | undefined): boolean {
  return isSet(d) && d.month > 0 && d.day > 0;
}

/** `today`: the user's today (`User.currentDate` when set), as the save uses. */
export function validateDraft(d: OnboardingDraft, today: PDate = todayLocal()): Issue[] {
  const out: Issue[] = [];
  const add = (path: string, message = REQUIRED) => out.push({ path, message });
  const amount = (path: string, v: number | undefined, required = true) => {
    if (v === undefined) {
      if (required) add(path);
    } else if (v < 0) add(path, "Enter a positive amount");
  };

  // Priorities
  d.priorities.forEach((p, i) => {
    if (!p.name.trim()) add(`priorities.${i}.name`, "Give it a name");
    if (!p.importance) add(`priorities.${i}.importance`, "Score it from 1 to 10");
    if (!isSet(p.when)) add(`priorities.${i}.when`, "Pick roughly when");
    if (p.cost !== undefined && p.cost < 0) add(`priorities.${i}.cost`, "Enter a positive amount");
    if (p.kind === PriorityKind.BUY_HOME) {
      // The legal minimum down payment is the engine's rule (shown from the save's errors).
      if (!p.housePrice || p.housePrice <= 0)
        add(`priorities.${i}.housePrice`, "Enter the home's price");
      else if (p.cost !== undefined && p.cost > p.housePrice)
        add(`priorities.${i}.cost`, "The down payment can't be more than the price");
      return;
    }
    if (p.repeats) {
      if (!isSet(p.until)) add(`priorities.${i}.until`, "Pick until when");
      else if (isSet(p.when) && compareDates(p.until, p.when) < 0)
        add(`priorities.${i}.until`, "This is before it starts");
    }
  });
  if (!d.retirementImportance) add("retirementImportance", "Score it from 1 to 10");

  // Household
  if (!d.self.givenName.trim()) add("self.givenName");
  if (!fullDate(d.self.dob)) add("self.dob", "Pick your birthday");
  else if (compareDates(d.self.dob, today) > 0) add("self.dob", "That's in the future");
  if (d.partnerStatus !== "single") {
    if (!d.partner.givenName.trim()) add("partner.givenName");
    if (!fullDate(d.partner.dob)) add("partner.dob", "Pick their birthday");
  }
  if (d.hasKids) {
    if (!d.kids.length) add("kids", "Add your children, or choose No");
    d.kids.forEach((k, i) => {
      if (!k.givenName.trim()) add(`kids.${i}.givenName`);
      if (!fullDate(k.dob)) add(`kids.${i}.dob`, "Pick their birthday");
    });
  }

  // Accounts (manual entry)
  d.accounts.forEach((a, i) => {
    amount(`accounts.${i}.balance`, a.balance);
    // a credit card's rate may be blank ("not sure"), as in Update Data
    if (a.rate !== undefined && (a.rate < 0 || a.rate > 100))
      add(`accounts.${i}.rate`, "Enter a rate between 0 and 100");
    if (a.kind === "resp" && d.hasKids && !a.beneficiaryUuids.length)
      add(`accounts.${i}.beneficiaryUuids`, "Pick who it's for");
  });

  // Spending
  amount("spending.essential", d.spending.essential);
  amount("spending.lifestyle", d.spending.lifestyle);
  amount("spending.rent", d.spending.rent, false);

  // Income
  d.incomes.forEach((inc, i) => amount(`incomes.${i}.amount`, inc.amount, false));
  // "When did you start at ...?" (a defined benefit pension's job): not after today, not before 14
  members(d).forEach((p, pi) => {
    const job = hasDbPension(d, p.uuid) ? pensionJob(d, p.uuid) : undefined;
    if (!job?.start) return;
    const who = pi ? p.givenName.trim() || "your partner" : "you";
    const issue = jobStartIssue(job.start, p.dob, today, who);
    if (issue) add(`incomes.${d.incomes.indexOf(job)}.start`, issue);
  });

  // What you owe: the same checks as Update Data (a blank rate is "not sure")
  if (d.hasProperty && !d.properties.length) add("properties", "Add your property, or choose No");
  if (d.hasDebts && !d.debts.length) add("debts", "Add the debt, or choose No");
  out.push(...validateOwed(d.hasProperty ? d.properties : [], d.hasDebts ? d.debts : [], today));

  // Safety net
  d.emergency.forEach((e, i) => {
    if (!e.whole) amount(`emergency.${i}.amount`, e.amount);
  });

  // "Retiring comfortably": an age someone has already reached (known once the birthdays are in)
  const retirement = draftRetirementAgeIssue(d, today);
  if (retirement) add("retirement.age", retirement);

  return out;
}

/** The same rules as a zod schema, for react-hook-form's resolver. */
export const draftSchema = z.custom<OnboardingDraft>().superRefine((draft, ctx) => {
  validateDraft(draft).forEach((issue) =>
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: issue.path.split("."),
      message: issue.message,
    }),
  );
});

/**
 * The form fields each step validates before moving on. An object's fields are named one by one:
 * react-hook-form keeps a nested object's errors from `trigger("self")` only when the object's own
 * fields are registered, and the draft registers none, so "self" would fail with nothing shown.
 */
export const STEP_FIELDS: Record<string, string[]> = {
  priorities: ["priorities", "retirementImportance", "retirement.age"],
  household: ["self.givenName", "self.dob", "partner.givenName", "partner.dob", "kids"],
  "everyday-accounts": ["accounts"],
  "investment-accounts": ["accounts"],
  spending: ["spending.essential", "spending.lifestyle", "spending.rent"],
  income: ["incomes"],
  debts: ["properties", "debts"],
  safety: ["emergency"],
};

/**
 * The names to re-check for `key`'s errors: the key itself, or for an object (`self`,
 * `spending`), each of its fields with an error (see `STEP_FIELDS` for why).
 */
export function errorNames(key: string, error: unknown): string[] {
  if (!error || typeof error !== "object" || Array.isArray(error)) return [key];
  if ("message" in error || "type" in error) return [key];
  return Object.keys(error).map((k) => `${key}.${k}`);
}
